import React from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useBuilderStore } from '../../store/builderStore';
import { SortableComponent } from './SortableComponent';

export const Canvas: React.FC = () => {
  const { t } = useTranslation('builder');
  const { currentPage, reorderComponents, isPreviewMode } = useBuilderStore();

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const components = currentPage?.components || [];
      const oldIndex = components.findIndex((c) => c.id === active.id);
      const newIndex = components.findIndex((c) => c.id === over.id);

      reorderComponents(oldIndex, newIndex);
    }
  };

  if (!currentPage) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <p className="text-gray-500 text-lg">{t('canvas.noPageLoaded')}</p>
          <p className="text-gray-400 text-sm mt-2">{t('canvas.noPageHint')}</p>
        </div>
      </div>
    );
  }

  // 📱 Match the published page's mobile canvas exactly so the editor is WYSIWYG.
  // For mobile layout: iPhone INNER content width = page.settings.maxPageWidth (default 480).
  // For fluid layout: drop the phone bezel and show a wider desktop-like canvas.
  const layoutWidth = currentPage.settings?.layoutWidth ?? 'mobile';
  const isFluid = layoutWidth === 'fluid';
  const contentWidth = isFluid
    ? 900
    : Math.max(320, Math.min(720, currentPage.settings?.maxPageWidth ?? 480));
  const bezel = isFluid ? 0 : 12;
  const frameOuterWidth = contentWidth + bezel * 2;
  // Modern iPhone aspect ratio ~19.5:9 → height ~ width * 2.167.
  // Cap so very wide canvases don't produce an absurdly tall mockup.
  const frameInnerHeight = isFluid
    ? 900
    : Math.min(980, Math.max(720, Math.round(contentWidth * 2.167)));

  return (
    <div className="flex-1 bg-gradient-to-br from-blue-400 via-purple-400 to-purple-500 overflow-auto">
      <div
        className="min-h-full flex items-center justify-center p-8"
        style={{ minWidth: 'fit-content' }}
      >
        {/* iPhone Mockup */}
        <div className="relative">
          {/* iPhone Frame (hidden for fluid/desktop layout) */}
          <div
            className={
              isFluid
                ? 'rounded-2xl shadow-2xl'
                : 'bg-gray-900 rounded-[3.5rem] p-3 shadow-2xl'
            }
            style={{ width: `${frameOuterWidth}px` }}
          >
            {/* Screen */}
            <div
              className={`theme-light-only ${isFluid ? 'rounded-2xl relative overflow-hidden' : 'rounded-[3rem] relative overflow-hidden'}`}
              style={{
                height: `${frameInnerHeight}px`,
                // ✅ ถ้าเป็น gradient ใช้ backgroundImage, ถ้าเป็นสี solid ใช้ background
                ...(currentPage.settings.backgroundColor?.startsWith('linear-gradient')
                  ? { 
                      backgroundImage: currentPage.settings.backgroundColor,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : currentPage.settings.backgroundImage
                    ? {
                        background: currentPage.settings.backgroundColor || '#ffffff',
                        backgroundImage: `url(${currentPage.settings.backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                      }
                    : {
                        background: currentPage.settings.backgroundColor || '#ffffff',
                      }
                ),
              }}
            >
              {/* Background Video */}
              {currentPage.settings.backgroundVideo && (
                <video
                  src={currentPage.settings.backgroundVideo}
                  className="absolute inset-0 w-full h-full object-cover z-0"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              )}
              {!isFluid && (
                <>
                  {/* iPhone Notch */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-900 rounded-b-3xl px-8 py-2 z-50">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-4 bg-gray-800 rounded-full"></div>
                    </div>
                  </div>

                  {/* Battery Indicator */}
                  <div className="absolute top-4 right-6 flex items-center gap-1 z-50">
                    <span className="text-xs font-semibold">100%</span>
                    <div className="w-6 h-3 border-2 border-black rounded-sm relative">
                      <div className="absolute inset-0.5 bg-green-500 rounded-sm"></div>
                      <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-1 h-2 bg-black rounded-r"></div>
                    </div>
                  </div>
                </>
              )}

              {/* Canvas Content */}
              <div
                className={`h-full overflow-y-auto relative z-10 ${isFluid ? '' : 'pt-8'}`}
              >
                {!isPreviewMode ? (
                  <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                      items={currentPage.components.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {currentPage.components.map((component) => (
                        <SortableComponent key={component.id} component={component} />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  currentPage.components.map((component) => (
                    <SortableComponent key={component.id} component={component} />
                  ))
                )}

                {currentPage.components.length === 0 && (
                  <div className="h-full flex items-center justify-center p-8">
                    <p className="text-gray-400 text-sm text-center">
                      {t('canvas.dragHint')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

